import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
    fetchDeviceCount,
    getHospitalCount,
    getActiveInfusionCountData,
    getHospitalNurseCount,
} from '@utils/utils';
import {
    HOSPITAL_DEVICE_TABLE,
    USER_INFO_TABLE,
    DEVICE_CONTROL_TABLE,
} from '@constants/dynamoDBTableName';
import { useAppDispatch, useAppSelector } from '@store/hooks';

/* ─── Icon Components ──────────────────────────────────────────────────── */

const DeviceIcon = () => (
    <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect x="6" y="2" width="16" height="24" rx="2" stroke="#2563EB" strokeWidth="2" fill="none"/>
        <line x1="10" y1="8" x2="18" y2="8" stroke="#2563EB" strokeWidth="2" strokeLinecap="round"/>
        <line x1="10" y1="12" x2="18" y2="12" stroke="#2563EB" strokeWidth="2" strokeLinecap="round"/>
        <line x1="10" y1="16" x2="15" y2="16" stroke="#2563EB" strokeWidth="2" strokeLinecap="round"/>
    </svg>
);

const ActivityIcon = () => (
    <svg width="22" height="22" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg">
        <polyline points="2,12 6,8 10,14 14,6 18,10 22,7" stroke="#2563EB" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
    </svg>
);

const NurseIcon = () => (
    <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="14" cy="8" r="4" stroke="#16A34A" strokeWidth="2" fill="none"/>
        <path d="M6 24c0-4.418 3.582-8 8-8s8 3.582 8 8" stroke="#16A34A" strokeWidth="2" strokeLinecap="round" fill="none"/>
        <line x1="18" y1="14" x2="22" y2="18" stroke="#16A34A" strokeWidth="2" strokeLinecap="round"/>
        <line x1="22" y1="14" x2="18" y2="18" stroke="#16A34A" strokeWidth="2" strokeLinecap="round"/>
    </svg>
);

const NurseCountIcon = () => (
    <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="10" cy="9" r="3.5" stroke="#16A34A" strokeWidth="1.8" fill="none"/>
        <path d="M3 23c0-3.866 3.134-7 7-7s7 3.134 7 7" stroke="#16A34A" strokeWidth="1.8" strokeLinecap="round" fill="none"/>
        <circle cx="20" cy="9" r="3.5" stroke="#16A34A" strokeWidth="1.8" fill="none" opacity="0.5"/>
        <path d="M20 16c2.761 0 5 2.239 5 5" stroke="#16A34A" strokeWidth="1.8" strokeLinecap="round" fill="none" opacity="0.5"/>
    </svg>
);

const InfusionIcon = () => (
    <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect x="9" y="2" width="10" height="14" rx="2" stroke="#7C3AED" strokeWidth="2" fill="none"/>
        <path d="M14 16 L14 26" stroke="#7C3AED" strokeWidth="2" strokeLinecap="round"/>
        <circle cx="14" cy="26" r="2" fill="#7C3AED"/>
        <line x1="11" y1="6" x2="11" y2="8" stroke="#7C3AED" strokeWidth="1.5" strokeLinecap="round"/>
        <line x1="9" y1="7" x2="13" y2="7" stroke="#7C3AED" strokeWidth="1.5" strokeLinecap="round"/>
    </svg>
);

const DropletIcon = () => (
    <svg width="22" height="22" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M11 2 C11 2 4 10 4 14.5 C4 18.09 7.13 21 11 21 C14.87 21 18 18.09 18 14.5 C18 10 11 2 11 2Z" stroke="#7C3AED" strokeWidth="1.8" fill="none" strokeLinejoin="round"/>
        <path d="M7.5 15 C7.5 13.07 9.07 11.5 11 11.5" stroke="#7C3AED" strokeWidth="1.5" strokeLinecap="round"/>
    </svg>
);

/* ─── Stat Card ────────────────────────────────────────────────────────── */

interface StatCardProps {
    title: string;
    count: number | null;
    unit: string;
    mainIcon: React.ReactNode;
    accentIcon: React.ReactNode;
    gradientFrom: string;
    gradientTo: string;
    iconBg: string;
    accentBg: string;
}

const StatCard: React.FC<StatCardProps> = ({
    title,
    count,
    unit,
    mainIcon,
    accentIcon,
    gradientFrom,
    gradientTo,
    iconBg,
    accentBg,
}) => (
    <div
        style={{
            background: '#fff',
            borderRadius: '16px',
            padding: '16px 20px 0 20px',
            flex: 1,
            minWidth: 0,
            boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            position: 'relative',
        }}
    >
        {/* Top Row */}
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '10px' }}>
            {/* Main Icon */}
            <div
                style={{
                    width: 52,
                    height: 52,
                    borderRadius: 14,
                    background: iconBg,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                }}
            >
                {mainIcon}
            </div>
            {/* Accent Icon */}
            <div
                style={{
                    width: 36,
                    height: 36,
                    borderRadius: 10,
                    background: accentBg,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                }}
            >
                {accentIcon}
            </div>
        </div>

        {/* Title */}
        <span style={{ fontSize: 13, color: '#94A3B8', fontWeight: 500, marginBottom: 4 }}>
            {title}
        </span>

        {/* Count */}
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, marginBottom: 14 }}>
            <span style={{ fontSize: 32, fontWeight: 700, color: '#0F172A', lineHeight: 1 }}>
                {count !== null ? count : '--'}
            </span>
            <span style={{ fontSize: 13, color: '#94A3B8', fontWeight: 500 }}>{unit}</span>
        </div>

        {/* Gradient Bottom Bar */}
        <div
            style={{
                height: 4,
                background: `linear-gradient(90deg, ${gradientFrom}, ${gradientTo})`,
                borderRadius: '0 0 4px 4px',
                marginLeft: -20,
                marginRight: -20,
            }}
        />
    </div>
);

/* ─── Main Component ───────────────────────────────────────────────────── */

const HospitalDashboardAnalytics = () => {
    const { t } = useTranslation();
    const [deviceCount, setDeviceCount] = useState<number | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [nurseCount, setNurseCount] = useState<number | null>(null);
    const [activeInfusionCount, setActiveInfusionCount] = useState<number | null>(null);
    const [loading, setLoading] = useState(true);
    const loggedInHospitalUuId = useAppSelector((state) => state.login.loggedInHospitalUuId);
    const loggedInHospitalId = useAppSelector((state) => state.login.loggedInHospitalId);

    const fetchDeviceCountData = async () => {
        try {
            const deviceCountTotal = await getHospitalCount(HOSPITAL_DEVICE_TABLE, loggedInHospitalUuId);
            const count = deviceCountTotal.Count ? deviceCountTotal.Count : 0;
            setDeviceCount(count);
        } catch (error: any) {
            setError(error);
        }
    };

    const fetchNurseCountData = async () => {
        try {
            const nurseCountTotal = await getHospitalNurseCount(USER_INFO_TABLE, loggedInHospitalId);
            const count = nurseCountTotal.Count ? nurseCountTotal.Count : 0;
            setNurseCount(count);
        } catch (error: any) {
            setError(error);
        }
    };

    const fetchActiveInfusionCountData = async () => {
        try {
            const activeInfusionCountTotal = await getActiveInfusionCountData(DEVICE_CONTROL_TABLE, loggedInHospitalUuId);
            const count = activeInfusionCountTotal.Items ? activeInfusionCountTotal.Items.length : 0;
            setActiveInfusionCount(count);
        } catch (error: any) {
            setError(error);
        }
    };

    useEffect(() => {
        const fetchData = async () => {
            await fetchActiveInfusionCountData();
            await fetchNurseCountData();
            await fetchDeviceCountData();
        };
        fetchData();
    }, []);

    return (
        <div
            style={{
                display: 'flex',
                gap: 16,
                marginTop: 20,
                marginBottom: 4,
                width: '100%',
            }}
        >
            {/* Total Device Card */}
            <StatCard
                title={t('hospital.total_device')}
                count={deviceCount}
                unit="Devices"
                mainIcon={<DeviceIcon />}
                accentIcon={<ActivityIcon />}
                gradientFrom="#2563EB"
                gradientTo="#60A5FA"
                iconBg="rgba(37,99,235,0.10)"
                accentBg="rgba(37,99,235,0.08)"
            />

            {/* Total Nurse Card */}
            <StatCard
                title={t('hospital.total_nurse')}
                count={nurseCount}
                unit="Nurses"
                mainIcon={<NurseCountIcon />}
                accentIcon={<NurseIcon />}
                gradientFrom="#16A34A"
                gradientTo="#4ADE80"
                iconBg="rgba(22,163,74,0.10)"
                accentBg="rgba(22,163,74,0.08)"
            />

            {/* Active Infusion Card */}
            <StatCard
                title={t('hospital.active_infusion')}
                count={activeInfusionCount}
                unit="Infusions"
                mainIcon={<InfusionIcon />}
                accentIcon={<DropletIcon />}
                gradientFrom="#7C3AED"
                gradientTo="#C084FC"
                iconBg="rgba(124,58,237,0.10)"
                accentBg="rgba(124,58,237,0.08)"
            />
        </div>
    );
};

export default HospitalDashboardAnalytics;
